import {
  CANCEL_OPEN_SHIFT_REQUEST_RPC,
  CLAIM_OPEN_SHIFT_SLOT_RPC,
  LIST_MY_OPEN_SHIFT_REQUESTS_RPC,
  OPEN_SHIFT_REQUEST_TEXT_MAX,
  REQUEST_OPEN_SHIFT_SLOT_RPC,
  asColorToken,
  asOpenFillMode,
  type ColorToken,
  type OpenFillMode,
} from "../../../../packages/shared/src/schedule-color.ts";
import {
  CLAIM_OPEN_SHIFT_FAILURES,
  mapOpenShiftRequests,
  resolveOpenShiftFailure,
  SELF_OPEN_SHIFT_REQUEST_FAILURES,
  type OpenShiftRequest,
} from "../../../../packages/shared/src/open-shift-requests.ts";
import { isMissingFeedSurface } from "../../../../packages/shared/src/feed.ts";

// Open Shifts (G6) - public.my_open_shifts (guard-facing eligible open slots, own-eligibility
// only) + the Wave 6A open-seat write paths. The view is the safety boundary: it never returns
// guard_user_id / assigned_by_user_id, and eligibility (active guard membership, schedule.self,
// published + future + live active client/site/route, not full, no overlapping own assignment)
// is enforced server-side. Read-only here; taking a seat goes through an RPC ONLY, never a
// table write.
//
// Note the view is declared `security_barrier` WITHOUT `security_invoker` (migration
// 20260611212518, carried into 20260808120000), so it runs definer-rights and base-table RLS
// is not a second gate. Its own WHERE clause is the caller binding - which is why the select
// list below stays an explicit allowlist and mapOpenShiftRow builds a fixed shape rather than
// spreading whatever the server sent.
//
// Wave 6A splits how a seat is taken, by the template's own policy:
//   instant_claim   -> claim_open_shift_slot   (existing, assignment created immediately)
//   request_approval-> request_open_shift_slot (a supervisor approves before the seat fills)
// The view projects `open_fill_mode` per row so the guard is never offered the wrong verb,
// and the server refuses the wrong one regardless of what this client renders.

export const OPEN_SHIFTS_VIEW = "my_open_shifts";
export const OPEN_SHIFTS_SELECT =
  "shift_slot_id,route_name,site_name,slot_date,starts_at,ends_at,required_credential_types";

/** Wave 6A adds the template's color label and fill policy to the guard view. */
export const OPEN_SHIFTS_SELECT_6C = `${OPEN_SHIFTS_SELECT},color_token,open_fill_mode`;

/** PostgREST/PostgreSQL undefined_column - the only signal that 6A has not landed here. */
const UNDEFINED_COLUMN = "42703";

export interface OpenShiftRow {
  shiftSlotId: string;
  routeName: string | null;
  siteName: string | null;
  slotDate: string | null;
  startsAt: string | null;
  endsAt: string | null;
  requiredCredentialTypes: string[];
  /** Template palette token; `slate` on a pre-6A server or an unknown value. */
  colorToken: ColorToken;
  /** How this seat fills; `instant_claim` on a pre-6A server or an unknown value. */
  openFillMode: OpenFillMode;
}

export interface OpenShiftsLoad {
  rows: OpenShiftRow[];
  /** True when the server predates 6A: every row reads slate / instant claim. */
  degraded: boolean;
}

export interface OpenShiftsQueryClient {
  from(table: string): {
    select(columns: string): {
      eq(
        column: string,
        value: string,
      ): {
        order(
          column: string,
          options: { ascending: boolean },
        ): {
          limit(
            count: number,
          ): Promise<{ data: unknown; error: { message: string; code?: string } | null }>;
        };
      };
    };
  };
}

export interface OpenShiftRpcClient {
  rpc(
    fn: string,
    args: Record<string, unknown>,
  ): Promise<{ data: unknown; error: { message: string } | null }>;
}

/** Kept as the old name so existing call sites and tests keep compiling. */
export type ClaimOpenShiftRpcClient = OpenShiftRpcClient;

function asString(value: unknown): string | null {
  return typeof value === "string" && value.length > 0 ? value : null;
}

function isUndefinedColumn(error: unknown): boolean {
  if (error === null || typeof error !== "object") return false;
  return (error as { code?: unknown }).code === UNDEFINED_COLUMN;
}

export function mapOpenShiftRow(raw: Record<string, unknown>): OpenShiftRow {
  return {
    shiftSlotId: asString(raw.shift_slot_id) ?? "",
    routeName: asString(raw.route_name),
    siteName: asString(raw.site_name),
    slotDate: asString(raw.slot_date),
    startsAt: asString(raw.starts_at),
    endsAt: asString(raw.ends_at),
    requiredCredentialTypes: Array.isArray(raw.required_credential_types)
      ? raw.required_credential_types.filter((item): item is string => typeof item === "string")
      : [],
    // Unknown token -> slate, unknown mode -> instant claim. The raw string is dropped here
    // and never reaches the DOM as a color.
    colorToken: asColorToken(raw.color_token),
    openFillMode: asOpenFillMode(raw.open_fill_mode),
  };
}

export const OPEN_SHIFTS_ROW_LIMIT = 50;

function mapRows(data: unknown): OpenShiftRow[] {
  const rows = Array.isArray(data) ? (data as unknown[]) : [];
  return rows
    .filter((row): row is Record<string, unknown> => typeof row === "object" && row !== null)
    .map(mapOpenShiftRow)
    .filter((row) => row.shiftSlotId !== "");
}

/**
 * The open board. Probe-then-fall-back: a pre-6A server answers 42703 for the two new
 * columns, and the board is re-read on the legacy list rather than going blank - a guard
 * losing color is a nuisance, a guard losing the shift list is a missed shift.
 */
export async function fetchOpenShifts(
  client: OpenShiftsQueryClient,
  accountId: string,
  limit: number = OPEN_SHIFTS_ROW_LIMIT,
): Promise<OpenShiftsLoad> {
  const read = (columns: string) =>
    client
      .from(OPEN_SHIFTS_VIEW)
      .select(columns)
      .eq("account_id", accountId)
      .order("starts_at", { ascending: true })
      .limit(limit);

  const extended = await read(OPEN_SHIFTS_SELECT_6C);
  if (!extended.error) {
    return { rows: mapRows(extended.data), degraded: false };
  }
  if (!isUndefinedColumn(extended.error)) {
    throw new Error("Could not load open shifts.");
  }
  const legacy = await read(OPEN_SHIFTS_SELECT);
  if (legacy.error) {
    throw new Error("Could not load open shifts.");
  }
  return { rows: mapRows(legacy.data), degraded: true };
}

// --- Write paths ------------------------------------------------------------------------

/** Fixed, SAFE failure copy - server internals never reach the officer's screen. */
export const SAFE_CLAIM_FAILURE =
  "Could not claim that shift. It may have just filled up - refresh and try again.";
export const SAFE_REQUEST_FAILURE =
  "Could not send that request. It may have just filled up - refresh and try again.";
export const SAFE_CANCEL_FAILURE = "Could not cancel that request - refresh and try again.";

export type OpenShiftActionResult =
  | { ok: true }
  | {
      ok: false;
      message: string;
      /** The board is out of date; refetch so the guard is not offered the wrong verb again. */
      stale: boolean;
    };

async function runOpenShiftWrite(
  client: OpenShiftRpcClient,
  fn: string,
  args: Record<string, unknown>,
  allowlist: ReadonlySet<string>,
  safeFallback: string,
): Promise<OpenShiftActionResult> {
  try {
    const { error } = await client.rpc(fn, args);
    if (error) {
      const { reason, stale } = resolveOpenShiftFailure(error.message, allowlist, safeFallback);
      return { ok: false, message: reason, stale };
    }
    return { ok: true };
  } catch {
    return { ok: false, message: safeFallback, stale: false };
  }
}

/** Instant claim: the assignment exists the moment this returns ok. */
export async function claimOpenShiftSlot(
  client: OpenShiftRpcClient,
  accountId: string,
  shiftSlotId: string,
): Promise<OpenShiftActionResult> {
  return runOpenShiftWrite(
    client,
    CLAIM_OPEN_SHIFT_SLOT_RPC,
    { p_account_id: accountId, p_shift_slot_id: shiftSlotId },
    CLAIM_OPEN_SHIFT_FAILURES,
    SAFE_CLAIM_FAILURE,
  );
}

/**
 * Request approval: nothing is assigned until a supervisor approves. The note is optional;
 * it is trimmed and dropped when empty because the server's own check constraint requires
 * a trimmed string of 1..500 characters when present.
 */
export async function requestOpenShiftSlot(
  client: OpenShiftRpcClient,
  accountId: string,
  shiftSlotId: string,
  note: string | null,
): Promise<OpenShiftActionResult> {
  const trimmed = note === null ? "" : note.trim();
  if (trimmed.length > OPEN_SHIFT_REQUEST_TEXT_MAX) {
    return {
      ok: false,
      message: "Open shift request note must be 500 characters or fewer.",
      stale: false,
    };
  }
  return runOpenShiftWrite(
    client,
    REQUEST_OPEN_SHIFT_SLOT_RPC,
    {
      p_account_id: accountId,
      p_shift_slot_id: shiftSlotId,
      p_request_note: trimmed.length === 0 ? null : trimmed,
    },
    SELF_OPEN_SHIFT_REQUEST_FAILURES,
    SAFE_REQUEST_FAILURE,
  );
}

/** Withdraw one's own pending request. Only `pending` transitions, server-enforced. */
export async function cancelOpenShiftRequest(
  client: OpenShiftRpcClient,
  accountId: string,
  requestId: string,
): Promise<OpenShiftActionResult> {
  return runOpenShiftWrite(
    client,
    CANCEL_OPEN_SHIFT_REQUEST_RPC,
    { p_account_id: accountId, p_request_id: requestId },
    SELF_OPEN_SHIFT_REQUEST_FAILURES,
    SAFE_CANCEL_FAILURE,
  );
}

export const MY_OPEN_SHIFT_REQUESTS_LIMIT = 50;

export interface MyOpenShiftRequestsLoad {
  requests: OpenShiftRequest[];
  /**
   * True when the read itself failed. Distinct from an empty list, because the Cancel
   * affordance hangs off these rows: "you have nothing waiting" and "we could not check
   * what you have waiting" send a guard to opposite conclusions, and only one of them is
   * a reason to stop looking for their request.
   */
  failed: boolean;
}

/**
 * The caller's own requests. A null status is intentional: rejected rows carry the desk's
 * reason and must remain visible after they leave the pending state. Never throws because
 * these rows annotate the open board and must not take the seat list down with them.
 *
 * A server without the request workflow at all is NOT a failure - there is genuinely
 * no request history on it.
 */
export async function fetchMyOpenShiftRequests(
  client: OpenShiftRpcClient,
  accountId: string,
  limit: number = MY_OPEN_SHIFT_REQUESTS_LIMIT,
): Promise<MyOpenShiftRequestsLoad> {
  try {
    const { data, error } = await client.rpc(LIST_MY_OPEN_SHIFT_REQUESTS_RPC, {
      p_account_id: accountId,
      p_limit: limit,
      p_status: null,
    });
    if (error) {
      return { requests: [], failed: !isMissingFeedSurface(error) };
    }
    return { requests: mapOpenShiftRequests(data), failed: false };
  } catch {
    return { requests: [], failed: true };
  }
}

/** Slot id -> the caller's pending request on it. At most one exists (unique partial index). */
export function indexRequestsBySlot(
  requests: readonly OpenShiftRequest[],
): Map<string, OpenShiftRequest> {
  const bySlot = new Map<string, OpenShiftRequest>();
  for (const request of requests) {
    if (request.shiftSlotId !== null && request.status === "pending") {
      bySlot.set(request.shiftSlotId, request);
    }
  }
  return bySlot;
}
